/** @shell standard @header bar
 * Allergies & Dislikes (v1): separate tabs; active when `app_config.allergy_picker_mode` is `split`.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHeader, useHeaderTopPadding } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { useAuthStore } from '../shared/auth/store';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useTranslation, getApiText } from '../shared/i18n';
import {
  useAllergiesQuery,
  useDislikesQuery,
  useSaveAllergiesMutation,
  useSaveDislikesMutation,
  useLegacySelectedAllergyIds,
  useLegacySelectedDislikeIds,
} from '../data/domains/preferences';
import { fetchProfileAndSyncUser } from '../data/domains/profile/api';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  createSelectableItemSmallSurfaceStyle,
  createSelectableItemSmallTitleStyle,
  createSegmentedGenderLabelStyle,
  createSegmentedGenderOptionStyle,
  segmentedGenderRow,
  selectableItemSmallContent,
  selectableItemSmallRow,
  selectableItemTrailingSpacer,
} from '../shared/theme/sharedStyles';
import { StackScreenShell, STACK_SCROLL_GUTTER } from '../shared/theme/studio/stackScreenShell';
import { useRequireVerified } from '../shared/auth/useRequireVerified';

const MAX_ALLERGIES = 30;
const MAX_DISLIKES = 30;
const SAVE_DEBOUNCE_MS = 500;
const HEADER_TABS_BAND_HEIGHT = 52;

type PreferenceRow = {
  id?: number;
  allergy_id?: number;
  dislike_id?: number;
  title?: string;
  a_title?: string;
  name?: string;
};

function getItemId(item: PreferenceRow, type: 'allergy' | 'dislike'): number {
  return item.id ?? (type === 'allergy' ? item.allergy_id : item.dislike_id) ?? 0;
}

function getItemLabel(item: PreferenceRow): string {
  return (getApiText(item, 'title', 'a_title') || item.title) ?? item.name ?? String(item.id ?? '');
}

function extractAllergiesList(data: unknown): PreferenceRow[] {
  const r = data as Record<string, unknown> | undefined;
  if (!r) {
    return [];
  }
  if (Array.isArray(r)) {
    return r as PreferenceRow[];
  }
  if (Array.isArray(r.allergies)) {
    return r.allergies as PreferenceRow[];
  }
  if (Array.isArray(r.data)) {
    return r.data as PreferenceRow[];
  }
  const nested = r.data as Record<string, unknown> | undefined;
  if (nested && Array.isArray(nested.allergies)) {
    return nested.allergies as PreferenceRow[];
  }
  return [];
}

function extractDislikesList(data: unknown): PreferenceRow[] {
  const r = data as Record<string, unknown> | undefined;
  if (!r) {
    return [];
  }
  if (Array.isArray(r)) {
    return r as PreferenceRow[];
  }
  if (Array.isArray(r.dislikes)) {
    return r.dislikes as PreferenceRow[];
  }
  if (Array.isArray(r.data)) {
    return r.data as PreferenceRow[];
  }
  const nested = r.data as Record<string, unknown> | undefined;
  if (nested && Array.isArray(nested.dislikes)) {
    return nested.dislikes as PreferenceRow[];
  }
  return [];
}

function createAllergiesDislikesStyles(c: ThemeColors) {
  return StyleSheet.create({
    body: {
      flex: 1,
    },
    tab_row_outer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      width: '100%',
    },
    scroll: {
      flex: 1,
    },
    scroll_content: {
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xl,
    },
    section: {
      marginBottom: appTheme.spacing.lg,
    },
    section_panel: {
      backgroundColor: c.surface,
      borderRadius: appTheme.borderRadius.xl,
      borderWidth: 1,
      borderColor: `${c.text}10`,
      padding: appTheme.spacing.md,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.07,
          shadowRadius: 12,
        },
        android: { elevation: 3 },
      }),
    },
    section_title: {
      ...appTheme.typography.caption,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: appTheme.spacing.sm,
      color: c.textSecondary,
    },
    card_list: {
      gap: appTheme.spacing.sm,
    },
    icon_well: {
      width: 40,
      height: 40,
      borderRadius: appTheme.borderRadius.md,
      justifyContent: 'center',
      alignItems: 'center',
      marginEnd: appTheme.spacing.sm,
    },
    loader_wrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl,
    },
    loader_text: {
      marginTop: appTheme.spacing.sm,
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    empty_wrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl,
    },
    empty_title: {
      ...appTheme.typography.h3,
      marginTop: appTheme.spacing.md,
      color: c.text,
    },
    empty_sub: {
      ...appTheme.typography.bodySmall,
      marginTop: appTheme.spacing.sm,
      textAlign: 'center',
      paddingHorizontal: appTheme.spacing.lg,
      color: c.textSecondary,
    },
    saving_row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      marginTop: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
    },
    saving_text: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
  });
}

export const AllergiesDislikesScreen: React.FC = () => {
  const { t } = useTranslation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  useRequireVerified('AllergiesDislikes');
  const theme_colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAllergiesDislikesStyles(theme_colors), [theme_colors]);
  const header_top_pad = useHeaderTopPadding(false, HEADER_TABS_BAND_HEIGHT);

  const customer_id = useAuthStore(s => s.user?.id);
  const [active_tab, set_active_tab] = useState<'allergies' | 'dislikes'>('allergies');

  const selected_allergy_ids = useLegacySelectedAllergyIds();
  const selected_dislike_ids = useLegacySelectedDislikeIds();

  const { data: allergies_data, isLoading: loading_allergies } = useAllergiesQuery(customer_id);
  const { data: dislikes_data, isLoading: loading_dislikes } = useDislikesQuery(customer_id);

  const save_allergies = useSaveAllergiesMutation();
  const save_dislikes = useSaveDislikesMutation();

  const allergies_list = useMemo(() => extractAllergiesList(allergies_data), [allergies_data]);
  const dislikes_list = useMemo(() => extractDislikesList(dislikes_data), [dislikes_data]);

  const [local_allergy_ids, set_local_allergy_ids] = useState<Set<number>>(
    () => new Set(selected_allergy_ids),
  );
  const [local_dislike_ids, set_local_dislike_ids] = useState<Set<number>>(
    () => new Set(selected_dislike_ids),
  );

  const save_allergies_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const save_dislikes_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const local_allergy_ids_ref = useRef(local_allergy_ids);
  const local_dislike_ids_ref = useRef(local_dislike_ids);
  local_allergy_ids_ref.current = local_allergy_ids;
  local_dislike_ids_ref.current = local_dislike_ids;

  useEffect(() => {
    set_local_allergy_ids(new Set(selected_allergy_ids));
  }, [selected_allergy_ids.join(',')]);

  useEffect(() => {
    set_local_dislike_ids(new Set(selected_dislike_ids));
  }, [selected_dislike_ids.join(',')]);

  useEffect(() => {
    if (typeof customer_id === 'number') {
      fetchProfileAndSyncUser(customer_id).catch(() => {});
    }
  }, [customer_id]);

  const schedule_save_allergies = useCallback(() => {
    if (save_allergies_timer_ref.current) {
      clearTimeout(save_allergies_timer_ref.current);
    }
    save_allergies_timer_ref.current = setTimeout(() => {
      save_allergies_timer_ref.current = null;
      save_allergies.mutate(Array.from(local_allergy_ids_ref.current));
    }, SAVE_DEBOUNCE_MS);
  }, [save_allergies]);

  const schedule_save_dislikes = useCallback(() => {
    if (save_dislikes_timer_ref.current) {
      clearTimeout(save_dislikes_timer_ref.current);
    }
    save_dislikes_timer_ref.current = setTimeout(() => {
      save_dislikes_timer_ref.current = null;
      save_dislikes.mutate(Array.from(local_dislike_ids_ref.current));
    }, SAVE_DEBOUNCE_MS);
  }, [save_dislikes]);

  useEffect(() => {
    return () => {
      if (save_allergies_timer_ref.current) {
        clearTimeout(save_allergies_timer_ref.current);
      }
      if (save_dislikes_timer_ref.current) {
        clearTimeout(save_dislikes_timer_ref.current);
      }
    };
  }, []);

  const toggle_allergy = useCallback(
    (id: number) => {
      set_local_allergy_ids(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < MAX_ALLERGIES) {
          next.add(id);
        }
        return next;
      });
      schedule_save_allergies();
    },
    [schedule_save_allergies],
  );

  const toggle_dislike = useCallback(
    (id: number) => {
      set_local_dislike_ids(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < MAX_DISLIKES) {
          next.add(id);
        }
        return next;
      });
      schedule_save_dislikes();
    },
    [schedule_save_dislikes],
  );

  const allergies_selected = useMemo(
    () => allergies_list.filter(item => local_allergy_ids.has(getItemId(item, 'allergy'))),
    [allergies_list, local_allergy_ids],
  );
  const allergies_available = useMemo(
    () => allergies_list.filter(item => !local_allergy_ids.has(getItemId(item, 'allergy'))),
    [allergies_list, local_allergy_ids],
  );
  const dislikes_selected = useMemo(
    () => dislikes_list.filter(item => local_dislike_ids.has(getItemId(item, 'dislike'))),
    [dislikes_list, local_dislike_ids],
  );
  const dislikes_available = useMemo(
    () => dislikes_list.filter(item => !local_dislike_ids.has(getItemId(item, 'dislike'))),
    [dislikes_list, local_dislike_ids],
  );

  const tab_allergies_surface = useMemo(
    () => createSegmentedGenderOptionStyle(theme_colors, { selected: active_tab === 'allergies' }),
    [theme_colors, active_tab],
  );
  const tab_dislikes_surface = useMemo(
    () => createSegmentedGenderOptionStyle(theme_colors, { selected: active_tab === 'dislikes' }),
    [theme_colors, active_tab],
  );
  const tab_allergies_label = useMemo(
    () => createSegmentedGenderLabelStyle(theme_colors, { selected: active_tab === 'allergies' }),
    [theme_colors, active_tab],
  );
  const tab_dislikes_label = useMemo(
    () => createSegmentedGenderLabelStyle(theme_colors, { selected: active_tab === 'dislikes' }),
    [theme_colors, active_tab],
  );

  const header_tabs = useMemo(
    () => (
      <View style={styles.tab_row_outer}>
        <View style={segmentedGenderRow}>
          <Clickable
            onPress={() => set_active_tab('allergies')}
            style={{ flex: 1 }}
            accessibilityLabel={t('account.allergies')}
            accessibilityRole="tab"
            accessibilityState={{ selected: active_tab === 'allergies' }}>
            <View style={tab_allergies_surface}>
              <Text style={tab_allergies_label}>{t('account.allergies')}</Text>
            </View>
          </Clickable>
          <Clickable
            onPress={() => set_active_tab('dislikes')}
            style={{ flex: 1 }}
            accessibilityLabel={t('account.dislikes')}
            accessibilityRole="tab"
            accessibilityState={{ selected: active_tab === 'dislikes' }}>
            <View style={tab_dislikes_surface}>
              <Text style={tab_dislikes_label}>{t('account.dislikes')}</Text>
            </View>
          </Clickable>
        </View>
      </View>
    ),
    [
      active_tab,
      styles.tab_row_outer,
      t,
      tab_allergies_label,
      tab_allergies_surface,
      tab_dislikes_label,
      tab_dislikes_surface,
    ],
  );

  const render_allergy_row = useCallback(
    (item: PreferenceRow, selected: boolean) => {
      const id = getItemId(item, 'allergy');
      const label = getItemLabel(item);
      const surface = createSelectableItemSmallSurfaceStyle(theme_colors, { selected });
      const title_style = createSelectableItemSmallTitleStyle(theme_colors, { selected });
      return (
        <Clickable
          key={`allergy-${id}`}
          onPress={() => toggle_allergy(id)}
          accessibilityLabel={label}
          accessibilityRole="button">
          <View style={[selectableItemSmallRow, surface]}>
            <View
              style={[
                styles.icon_well,
                {
                  backgroundColor: selected
                    ? (theme_colors.primaryUltra ?? theme_colors.primaryFade)
                    : theme_colors.background,
                  borderWidth: StyleSheet.hairlineWidth,
                  borderColor: theme_colors.borderLight,
                },
              ]}>
              <SvgIcon
                name="medkit"
                size={20}
                color={selected ? theme_colors.primary : theme_colors.textSecondary}
              />
            </View>
            <View style={selectableItemSmallContent}>
              <Text style={title_style} numberOfLines={2}>
                {label}
              </Text>
            </View>
            {selected ? (
              <SvgIcon name="check" size={22} color={theme_colors.primary} />
            ) : (
              <View style={selectableItemTrailingSpacer} />
            )}
          </View>
        </Clickable>
      );
    },
    [styles.icon_well, theme_colors, toggle_allergy],
  );

  const render_dislike_row = useCallback(
    (item: PreferenceRow, selected: boolean) => {
      const id = getItemId(item, 'dislike');
      const label = getItemLabel(item);
      const surface = createSelectableItemSmallSurfaceStyle(theme_colors, { selected });
      const title_style = createSelectableItemSmallTitleStyle(theme_colors, { selected });
      return (
        <Clickable
          key={`dislike-${id}`}
          onPress={() => toggle_dislike(id)}
          accessibilityLabel={label}
          accessibilityRole="button">
          <View style={[selectableItemSmallRow, surface]}>
            <View
              style={[
                styles.icon_well,
                {
                  backgroundColor: selected
                    ? (theme_colors.primaryUltra ?? theme_colors.primaryFade)
                    : theme_colors.background,
                  borderWidth: StyleSheet.hairlineWidth,
                  borderColor: theme_colors.borderLight,
                },
              ]}>
              <SvgIcon
                name="nomeal"
                size={20}
                color={selected ? theme_colors.primary : theme_colors.textSecondary}
              />
            </View>
            <View style={selectableItemSmallContent}>
              <Text style={title_style} numberOfLines={2}>
                {label}
              </Text>
            </View>
            {selected ? (
              <SvgIcon name="check" size={22} color={theme_colors.primary} />
            ) : (
              <View style={selectableItemTrailingSpacer} />
            )}
          </View>
        </Clickable>
      );
    },
    [styles.icon_well, theme_colors, toggle_dislike],
  );

  return (
    <StackScreenShell colors={theme_colors}>
      <ScreenHeader
        title={t('account.allergiesAndDislikes')}
        onBack={on_back_exit}
        bottomContent={header_tabs}
      />

      <View style={[styles.body, { paddingTop: header_top_pad }]}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.scroll_content,
            {
              paddingHorizontal: STACK_SCROLL_GUTTER,
              paddingBottom: insets.bottom + appTheme.spacing.lg,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          {active_tab === 'allergies' ? (
            loading_allergies ? (
              <View style={styles.loader_wrap}>
                <MainLoader size={48} color={theme_colors.primary} />
                <Text style={styles.loader_text}>{t('common.loading')}</Text>
              </View>
            ) : allergies_list.length === 0 ? (
              <View style={styles.empty_wrap}>
                <SvgIcon name="medkit" size={48} color={theme_colors.textLight} />
                <Text style={styles.empty_title}>{t('account.allergies')}</Text>
                <Text style={styles.empty_sub}>{t('account.noAllergiesList')}</Text>
              </View>
            ) : (
              <>
                {allergies_selected.length > 0 ? (
                  <View style={styles.section}>
                    <Text style={styles.section_title}>
                      {t('account.selected')} ({allergies_selected.length})
                    </Text>
                    <View style={styles.section_panel}>
                      <View style={styles.card_list}>
                        {allergies_selected.map(item => render_allergy_row(item, true))}
                      </View>
                    </View>
                  </View>
                ) : null}
                {allergies_available.length > 0 ? (
                  <View style={styles.section}>
                    <Text style={styles.section_title}>
                      {t('account.available')} ({allergies_available.length})
                    </Text>
                    <View style={styles.section_panel}>
                      <View style={styles.card_list}>
                        {allergies_available.map(item => render_allergy_row(item, false))}
                      </View>
                    </View>
                  </View>
                ) : null}
                {save_allergies.isPending ? (
                  <View style={styles.saving_row}>
                    <MainLoader size={18} color={theme_colors.primary} />
                    <Text style={styles.saving_text}>{t('account.saving')}</Text>
                  </View>
                ) : null}
              </>
            )
          ) : loading_dislikes ? (
            <View style={styles.loader_wrap}>
              <MainLoader size={48} color={theme_colors.primary} />
              <Text style={styles.loader_text}>{t('common.loading')}</Text>
            </View>
          ) : dislikes_list.length === 0 ? (
            <View style={styles.empty_wrap}>
              <SvgIcon name="nomeal" size={48} color={theme_colors.textLight} />
              <Text style={styles.empty_title}>{t('account.dislikes')}</Text>
              <Text style={styles.empty_sub}>{t('account.noDislikesList')}</Text>
            </View>
          ) : (
            <>
              {dislikes_selected.length > 0 ? (
                <View style={styles.section}>
                  <Text style={styles.section_title}>
                    {t('account.selected')} ({dislikes_selected.length})
                  </Text>
                  <View style={styles.section_panel}>
                    <View style={styles.card_list}>
                      {dislikes_selected.map(item => render_dislike_row(item, true))}
                    </View>
                  </View>
                </View>
              ) : null}
              {dislikes_available.length > 0 ? (
                <View style={styles.section}>
                  <Text style={styles.section_title}>
                    {t('account.available')} ({dislikes_available.length})
                  </Text>
                  <View style={styles.section_panel}>
                    <View style={styles.card_list}>
                      {dislikes_available.map(item => render_dislike_row(item, false))}
                    </View>
                  </View>
                </View>
              ) : null}
              {save_dislikes.isPending ? (
                <View style={styles.saving_row}>
                  <MainLoader size={18} color={theme_colors.primary} />
                  <Text style={styles.saving_text}>{t('account.saving')}</Text>
                </View>
              ) : null}
            </>
          )}
        </ScrollView>
      </View>
    </StackScreenShell>
  );
};

export default AllergiesDislikesScreen;

/*
 * V3 combined picker — see `AllergiesDislikesScreenV3.tsx`.
 * Toggle import when `combined_allergy_api` capability gate is enabled:
 *
 * import { AllergiesDislikesScreenV3 } from './AllergiesDislikesScreenV3';
 * export default AllergiesDislikesScreenV3;
 */
